import React from "react";
import CodePill from "../../../../shared/CodePill/CodePill";
import { colorForPrefix, toHex7 } from "../../../../../utils/color";
import { setCountDragImage, setDragIds } from "../../../../../utils/drag";
import type { Trade, WorkCodeCandidate } from "../../../../../models";
import type { CodeSource, CodeGroup } from "../codeGroups";
import "./CodeRow.css";

interface Props {
  group: CodeGroup;
  selected: boolean;
  expanded: boolean;
  loading: boolean;
  /** Activities carrying this code (lazy-loaded on expand). */
  activities: WorkCodeCandidate[] | undefined;
  /** All tenant trades (for the dropdown) + this group's effective trade. */
  trades: Trade[];
  trade: Trade | null;
  /** The code is in different trades across its sections — the row can't show
   *  a single one. The dropdown still sets ALL sections at once. */
  tradeMixed: boolean;
  /** Tint the whole row with the trade color instead of coloring the pill. */
  colorByTrade: boolean;
  onToggleSelect: (key: string) => void;
  onToggleExpand: (key: string) => void;
  /** Apply an edit to every source in the group (rename/recolor/recode). */
  onEditGroup: (sources: CodeSource[], patch: { newCode?: string; name?: string; color?: string }) => void;
  /** Stage this group's trade across ALL its sections (null = unassign). */
  onSetTrade: (tradeId: number | null) => void;
  /** Drag identity — the selection when this row is part of it, else itself. */
  getDragIds: (key: string) => string[];
}

const CodeRow: React.FC<Props> = ({
  group,
  selected,
  expanded,
  loading,
  activities,
  trades,
  trade,
  tradeMixed,
  colorByTrade,
  onToggleSelect,
  onToggleExpand,
  onEditGroup,
  onSetTrade,
  getDragIds,
}) => {
  // Fall back to a hue derived from the SOURCE code (stable + matches the board,
  // which colors per Takt_Work), not the live effective code/name — so the
  // swatch doesn't jump while editing.
  const color = group.color || colorForPrefix(group.sources[0]?.code || group.code);
  // The inputs are controlled straight off the (effective) group. The group's
  // `key` is stable across edits — derived from the source code|prefix, never
  // from the edited value — so the row never remounts and the inputs keep focus
  // while typing. editWorkCode stores exactly what's typed (no live-trim/empty-
  // drop), so the value tracks the field and an undo flows straight back in.

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  // Normalize on blur (NOT per keystroke — that would snap the cursor): trim,
  // and an emptied field reverts to source (`undefined` drops the edit).
  const blurCode = () => {
    const t = group.code.trim();
    if (t !== group.code) onEditGroup(group.sources, { newCode: t || undefined });
  };
  const blurName = () => {
    const t = group.name.trim();
    if (t !== group.name) onEditGroup(group.sources, { name: t || undefined });
  };
  const expand = (e: React.SyntheticEvent) => {
    stop(e);
    onToggleExpand(group.key);
  };

  const handleDragStart = (e: React.DragEvent) => {
    const ids = getDragIds(group.key);
    setDragIds(e, ids);
    if (ids.length > 1) setCountDragImage(e, `${ids.length} codes`);
  };

  // Color-by-trade: the row carries the trade's colors (pill goes neutral so
  // it doesn't fight); unassigned rows stay plain.
  const tinted = colorByTrade && trade != null;
  const rowStyle: React.CSSProperties | undefined = tinted
    ? { backgroundColor: trade.fill_color, color: trade.font_color }
    : undefined;

  return (
    <div className={`takt-editor-code ${selected ? "is-selected" : ""}`}>
      {/* Click anywhere on the row to SELECT it (chevron/pencil/checkbox opt
          out); drag it onto a trade bucket to assign. */}
      <div
        className={`takt-editor-code-main ${tinted ? "is-tinted" : ""}`}
        style={rowStyle}
        onClick={() => onToggleSelect(group.key)}
        draggable
        onDragStart={handleDragStart}
      >
        <input
          type="checkbox"
          className="takt-editor-code-check"
          checked={selected}
          onClick={stop}
          onChange={() => onToggleSelect(group.key)}
          aria-label="Select work code"
        />
        <CodePill
          code={group.code || "—"}
          color={colorByTrade ? "#666" : color}
          className="takt-editor-code-pill"
        />
        <span className="takt-editor-code-name" title={group.name || group.code}>
          {group.name || group.code}
        </span>
        <button
          type="button"
          className={`takt-editor-code-pencil ${expanded ? "is-active" : ""}`}
          onClick={expand}
          title="Edit code / trade / show activities"
          aria-label="Edit work code"
        >
          ✎
        </button>
        <button
          type="button"
          className={`takt-editor-code-actlink ${expanded ? "is-active" : ""}`}
          onClick={expand}
          title="Show the activities carrying this code, and edit it"
        >
          {group.activityCount} activities
        </button>
        <span className="takt-editor-code-trade" title="Drag the row onto a trade bucket, or use the ✎ form">
          Trade:{" "}
          {tradeMixed ? (
            <span className="takt-editor-code-trade-name" title="Different trades across sections — use the ✕ on bucket chips to adjust per section">
              Mixed
            </span>
          ) : trade ? (
            <>
              {!tinted && (
                <span
                  className="takt-editor-code-trade-swatch"
                  style={{ backgroundColor: trade.fill_color }}
                />
              )}
              <span className="takt-editor-code-trade-name">{trade.name}</span>
            </>
          ) : (
            <span className="takt-editor-code-trade-none">N/A</span>
          )}
        </span>
        <span className="takt-editor-code-spacer" aria-hidden />
        {group.sources.length > 1 && (
          <span className="takt-editor-code-merged" title="Merged from several codes">
            merged ×{group.sources.length}
          </span>
        )}
        {group.sections.length > 0 && (
          <span className="takt-editor-code-sections" title={group.sections.join(", ")}>
            {group.sections.length === 1 ? group.sections[0] : `${group.sections.length} sections`}
          </span>
        )}
      </div>

      {expanded && (
        <div className="takt-editor-code-expand">
          <div className="takt-editor-code-form">
            <label className="takt-editor-code-field">
              code
              <input
                value={group.code}
                onChange={(e) => onEditGroup(group.sources, { newCode: e.target.value })}
                onBlur={blurCode}
                aria-label="Work code value"
              />
            </label>
            <label className="takt-editor-code-field">
              name
              <input
                value={group.name}
                onChange={(e) => onEditGroup(group.sources, { name: e.target.value })}
                onBlur={blurName}
                aria-label="Work code name"
              />
            </label>
            <label className="takt-editor-code-field takt-editor-code-field--color">
              color
              <input
                type="color"
                value={toHex7(color)}
                onChange={(e) => onEditGroup(group.sources, { color: e.target.value })}
                aria-label="Work code color"
              />
            </label>
            <label className="takt-editor-code-field takt-editor-code-field--trade">
              trade {tradeMixed && <span className="takt-editor-code-trade-mixedhint">(mixed → sets all sections)</span>}
              <select
                value={tradeMixed ? "__mixed" : trade?.id ?? ""}
                onChange={(e) =>
                  onSetTrade(e.target.value === "" ? null : Number(e.target.value))
                }
                aria-label="Trade"
              >
                {tradeMixed && (
                  <option value="__mixed" disabled>
                    Mixed — pick a trade to set all
                  </option>
                )}
                <option value="">N/A — no trade</option>
                {trades.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="takt-editor-code-activities">
            {loading && <div className="takt-editor-hint">Loading…</div>}
            {!loading && activities && activities.length === 0 && (
              <div className="takt-editor-hint">No activities carry this code.</div>
            )}
            {!loading && activities && activities.length > 0 && (
              <div className="takt-editor-code-actlist">
                {activities.map((a) => (
                  <div key={a.id} className="takt-editor-code-act">
                    <CodePill code={group.code || "—"} color={color} className="takt-editor-code-actpill" />
                    <span className="takt-editor-code-actname">{a.task_name}</span>
                    {a.section_name && (
                      <span className="takt-editor-code-actsection">{a.section_name}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeRow;
